import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getChartData } from '../../services/api';
import { MiniChartSkeleton } from './ChartSkeleton';

/**
 * TickerHoverPreview Component
 *
 * Displays a mini chart preview when hovering over ticker links.
 * Shows current price, % change, and a sparkline chart.
 *
 * Features:
 * - Hover-triggered preview popup
 * - Cached data for instant display
 * - Sparkline chart visualization
 * - Quick actions dropdown
 * - Smooth animations
 */

// Simple in-memory cache for preview data
const previewCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Sparkline Chart Component
 * Lightweight SVG chart for preview
 */
const SparklineChart = ({ data, width = 180, height = 60 }) => {
  if (!data || data.length === 0) return null;

  const prices = data.map(d => d.close);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;

  const points = prices.map((price, i) => {
    const x = (i / (prices.length - 1)) * width;
    const y = height - ((price - min) / range) * height;
    return { x, y };
  });

  const pathD = points.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
  ).join(' ');

  const firstPrice = prices[0];
  const lastPrice = prices[prices.length - 1];
  const isPositive = lastPrice >= firstPrice;

  return (
    <svg width={width} height={height} className="sparkline-chart">
      <defs>
        <linearGradient id={`gradient-${isPositive ? 'up' : 'down'}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.4"/>
          <stop offset="100%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity="0.05"/>
        </linearGradient>
      </defs>

      {/* Area fill */}
      <path
        d={`${pathD} L ${width},${height} L 0,${height} Z`}
        fill={`url(#gradient-${isPositive ? 'up' : 'down'})`}
      />

      {/* Line */}
      <path
        d={pathD}
        stroke={isPositive ? '#10b981' : '#ef4444'}
        strokeWidth={1.5}
        fill="none"
      />
    </svg>
  );
};

/**
 * Main TickerHoverPreview Component
 */
function TickerHoverPreview({ ticker, children, showDropdown = false }) {
  const [showPreview, setShowPreview] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef(null);
  const linkRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const fetchPreviewData = async () => {
    // Check cache first
    const cached = previewCache.get(ticker);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setPreviewData(cached.data);
      return;
    }

    setLoading(true);
    try {
      const response = await getChartData(ticker, '1m', []);
      const chartData = response.data;

      if (chartData && chartData.chart_data && chartData.chart_data.length > 0) {
        const data = chartData.chart_data;
        const currentPrice = data[data.length - 1].close;
        const previousPrice = data[0].close;
        const change = currentPrice - previousPrice;
        const changePercent = (change / previousPrice) * 100;

        const previewInfo = {
          currentPrice,
          change,
          changePercent,
          chartData: data
        };

        setPreviewData(previewInfo);

        // Cache the data
        previewCache.set(ticker, {
          data: previewInfo,
          timestamp: Date.now()
        });
      }
    } catch (error) {
      console.error('Error fetching preview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = (e) => {
    if (linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      setPosition({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 10
      });
    }

    // Delay preview to avoid flickering
    timeoutRef.current = setTimeout(() => {
      setShowPreview(true);
      fetchPreviewData();
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowPreview(false);
    setShowActions(false);
  };

  const handleClick = (e) => {
    if (showDropdown) {
      e.preventDefault();
      setShowActions(!showActions);
    }
  };

  return (
    <div className="ticker-hover-preview-container" ref={linkRef}>
      {/* Ticker Link */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="inline-block"
      >
        {children}
      </div>

      {/* Preview Popup */}
      {showPreview && !showActions && (
        <div
          className="ticker-preview-popup fixed z-50 pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="preview-content bg-slate-900/95 backdrop-blur-md border border-slate-700/80 rounded-lg shadow-2xl shadow-black/50 p-3">
            {loading || !previewData ? (
              <MiniChartSkeleton />
            ) : (
              <div className="w-[200px]">
                {/* Header */}
                <div className="mb-2">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold text-slate-100">{ticker}</span>
                    <span className="text-lg font-semibold text-slate-100">
                      ${previewData.currentPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className={`text-xs font-medium ${previewData.changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {previewData.changePercent >= 0 ? '+' : ''}
                    {previewData.changePercent.toFixed(2)}%
                    <span className="ml-1">
                      ({previewData.change >= 0 ? '+' : ''}{previewData.change.toFixed(2)})
                    </span>
                  </div>
                </div>

                {/* Sparkline */}
                <div className="mb-2">
                  <SparklineChart data={previewData.chartData} />
                </div>

                {/* Footer */}
                <div className="text-xs text-slate-400 text-center">
                  Last 30 days • Click to view dashboard
                </div>
              </div>
            )}

            {/* Arrow pointer */}
            <div className="absolute -top-1.5 left-1/2 transform -translate-x-1/2">
              <div className="w-3 h-3 bg-slate-900/95 border-l border-t border-slate-700/80 rotate-45"></div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions Dropdown */}
      {showActions && showDropdown && (
        <div
          className="ticker-actions-dropdown fixed z-50"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translateX(-50%)'
          }}
          onMouseLeave={handleMouseLeave}
        >
          <div className="dropdown-content bg-slate-900/95 backdrop-blur-md border border-slate-700/80 rounded-lg shadow-2xl shadow-black/50 py-1 min-w-[180px]">
            <Link
              to={`/dashboard/${ticker}`}
              target="_blank"
              rel="noopener noreferrer"
              className="action-item flex items-center gap-2 px-4 py-2 text-sm text-slate-100 hover:bg-slate-800 transition-colors"
            >
              <span>📊</span>
              <span>Dashboard</span>
            </Link>

            <a
              href={`https://finance.yahoo.com/quote/${ticker}`}
              target="_blank"
              rel="noopener noreferrer"
              className="action-item flex items-center gap-2 px-4 py-2 text-sm text-slate-100 hover:bg-slate-800 transition-colors"
            >
              <span>📰</span>
              <span>News & Info</span>
            </a>

            <button
              onClick={() => navigator.clipboard.writeText(ticker)}
              className="action-item flex items-center gap-2 px-4 py-2 text-sm text-slate-100 hover:bg-slate-800 transition-colors w-full text-left"
            >
              <span>📋</span>
              <span>Copy Ticker</span>
            </button>
          </div>

          {/* Arrow pointer */}
          <div className="absolute -top-1.5 left-1/2 transform -translate-x-1/2">
            <div className="w-3 h-3 bg-slate-900/95 border-l border-t border-slate-700/80 rotate-45"></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TickerHoverPreview;
